import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import React, { Fragment, useEffect, useState } from 'react'
import Media from 'react-media'
import useWindowSize from 'react-use/lib/useWindowSize'
import { CSSTransition } from 'react-transition-group'

import { CarouselArrowIcon } from '@/components/ui/icons'
import DayIcon from '@/assets/images/icons/day.png'
import NightIcon from '@/assets/images/icons/night.png'

import { desktop, mobile } from './media-query'
import { Phone } from './phone'
import { Toggle } from './toggle'
import { slide2Stickers, slide3Stickers, slideContent } from './slide-data'

const CarouselContainer = styled.div`
  height: 588px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
  position: relative;

  @media ${mobile.query} {
    flex-direction: column;
    margin: 0;
  }
`

const PhoneGraphicContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  min-width: 300px;
  margin: 0 8px;

  @media ${mobile.query} {
    min-width: unset;
  }
`

const Runner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 80%;
`

type StickersProps = {
  isInView?: boolean
}

const Stickers = styled.span<StickersProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  visibility: ${({ isInView }) => (isInView ? 'visible' : 'hidden')};
  pointer-events: none;
`

type StickerImgProps = {
  addedStyle: FlattenSimpleInterpolation
}

const StickerImg = styled.img<StickerImgProps>`
  position: absolute;
  transform: scale(0.01);
  ${({ addedStyle }) => addedStyle}
`

const Arrow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  z-index: 1;
  box-sizing: border-box;
  padding: 0 25px;
  width: 70px;
  cursor: pointer;
`

const Breadcrumbs = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 85%;
  left: 0;

  h6 {
    font-family: 'PT Mono';
  }
`

const Lines = styled.div`
  display: flex;

  span {
    display: flex;
    align-items: center;
    height: 15px;
    margin-right: 5px;
    cursor: pointer;
  }
`

type LineProps = {
  isActive?: boolean
}

const Line = styled.div<LineProps>`
  width: 70px;
  height: 3px;
  background: black;
  opacity: 0.25;

  &:last-child {
    margin-right: 0;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      opacity: 1;
      transition: opacity 300ms ease-in-out;
    `}
`

const MobileSlide = styled.div`
  width: 100%;
  background-image: ${({ theme }) =>
    `linear-gradient(244deg, ${theme.palette.giphyGreen} 0%, ${theme.palette.giphyPurple} 100%)`};
  background-position: 95% center;
  background-size: 828px;
  height: 465px;
  display: flex;
  position: relative;
  justify-content: center;
`

const Slides = styled.div`
  display: flex;
  height: 100%;
  background-image: ${({ theme }) =>
    `linear-gradient(-116.56505117707799deg, ${theme.palette.giphyGreen} 0%, ${theme.palette.giphyPurple} 100%)`};
  background-size: 300% 300%;
  width: 100%;
`

const Slide = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
`

const SlideContainer = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.containerWidth}px;
  display: flex;
  justify-content: space-between;
  flex: 1;
  position: relative;
`

const SlideEnd = styled.div<{ currentSlideIndex: number }>`
  max-width: 326px;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding-top: ${({ currentSlideIndex }) => (currentSlideIndex == 1 ? '68px' : undefined)};

  &.fade-exit {
    opacity: 0;
  }

  &.fade-exit-active {
    opacity: 1;
    transition: opacity 200ms ease-in-out;
  }

  p {
    opacity: 0.8;
    color: ${({ theme }) => theme.palette.giphyWhite};
  }

  h3 {
    line-height: 26px;
    max-width: 285px;
    width: 100%;
  }
`

const Toggles = styled.div`
  width: 100%;
  margin-top: 20px;

  @media ${mobile.query} {
    width: 274px;
  }
`

export const THEME = { dark: 'Dark', light: 'Light' }
const SLIDECOUNT = 3

const Carousel = () => {
  const [mounted, setMounted] = useState(false)
  const [count, setCount] = useState(0)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [theme, setTheme] = useState(THEME.dark)
  const [showSecondSlideEmojis, setShowSecondSlideEmojis] = useState(false)
  const [showThirdSlideEmojis, setShowThirdSlideEmojis] = useState(false)
  const { width } = useWindowSize()

  useEffect(() => setMounted(true), [])
  useEffect(() => {
    // javascript does not handle modular arithmetic with negative numbers correctly
    setCurrentSlideIndex(((count % SLIDECOUNT) + SLIDECOUNT) % SLIDECOUNT)
    setIsAnimating(true)
  }, [count])

  useEffect(() => {
    // when the window is smaller than mobile threshold, default to the first slide
    if (window.matchMedia(mobile.query).matches) {
      setCurrentSlideIndex(0)
    }
  }, [width])

  useEffect(() => {
    const isSecondSlide = currentSlideIndex === 1
    const isThirdSlide = currentSlideIndex === 2

    // delay setting of this so animation plays every time it comes into view
    setTimeout(() => {
      setShowSecondSlideEmojis(isSecondSlide)
    }, 0)
    setTimeout(() => {
      setShowThirdSlideEmojis(isThirdSlide)
    }, 0)

    setIsAnimating(false)
  }, [currentSlideIndex])

  const currentSlide = slideContent[currentSlideIndex]
  const currentSlideStickers = [
    [],
    slide2Stickers(showSecondSlideEmojis),
    slide3Stickers(showThirdSlideEmojis),
  ]
  const showSlideEmojis = showSecondSlideEmojis || showThirdSlideEmojis

  const getToggles = () => {
    return (
      <Toggles>
        <div>
          <Toggle
            icon1={NightIcon}
            icon2={DayIcon}
            name={'Theme'}
            onChange={setTheme}
            option1={THEME.dark}
            option2={THEME.light}
            value={theme}
          />
        </div>
      </Toggles>
    )
  }

  return (
    <Media query={desktop.query}>
      {mounted
        ? (matches) => (
            <Fragment>
              <CarouselContainer>
                {matches ? (
                  <Fragment>
                    <Runner>
                      <Slides
                        style={{
                          backgroundPosition: `${Math.min(50 * currentSlideIndex, 80)}% 50%`,
                          transition: 'background-position 500ms ease-in-out',
                        }}
                      >
                        <Slide>
                          <Arrow
                            onClick={() => {
                              setCount(count - 1)
                            }}
                          >
                            <CarouselArrowIcon />
                          </Arrow>
                          <SlideContainer>
                            <Breadcrumbs>
                              <h6>
                                {currentSlideIndex + 1}/{slideContent.length}
                              </h6>
                              <Lines>
                                {slideContent.map((_, index) => (
                                  <span
                                    onClick={() => {
                                      setCount(index)
                                    }}
                                    key={index}
                                  >
                                    <Line isActive={index === currentSlideIndex} />
                                  </span>
                                ))}
                              </Lines>
                            </Breadcrumbs>
                            <Stickers isInView={showSlideEmojis}>
                              {currentSlideStickers[currentSlideIndex].map((sticker) => (
                                <StickerImg
                                  key={sticker.src}
                                  src={sticker.src}
                                  addedStyle={sticker.className}
                                />
                              ))}
                            </Stickers>
                            <CSSTransition in={isAnimating} timeout={200} classNames="fade">
                              <SlideEnd currentSlideIndex={currentSlideIndex}>
                                <h3>{currentSlide.leftHeader}</h3>
                                <p>{currentSlide.leftText}</p>
                              </SlideEnd>
                            </CSSTransition>
                            <PhoneGraphicContainer>
                              <Phone theme={theme} currentSlideIndex={currentSlideIndex} />
                            </PhoneGraphicContainer>
                            <CSSTransition in={isAnimating} timeout={200} classNames="fade">
                              <SlideEnd currentSlideIndex={currentSlideIndex}>
                                {currentSlide.rightHeader && <h3>{currentSlide.rightHeader}</h3>}
                                {currentSlide.rightText && <p>{currentSlide.rightText}</p>}
                                {currentSlide.isToggle && getToggles()}
                              </SlideEnd>
                            </CSSTransition>
                          </SlideContainer>
                          <Arrow
                            onClick={() => {
                              setCount(count + 1)
                            }}
                          >
                            <CarouselArrowIcon rotate={180} />
                          </Arrow>
                        </Slide>
                      </Slides>
                    </Runner>
                  </Fragment>
                ) : (
                  <Fragment>
                    <MobileSlide>
                      <Phone theme={theme} currentSlideIndex={currentSlideIndex} />
                    </MobileSlide>
                    {getToggles()}
                  </Fragment>
                )}
              </CarouselContainer>
            </Fragment>
          )
        : null}
    </Media>
  )
}

export default Carousel
